import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { openFile, useBoolean } from '@/_shared';

import { useCreateFileMutation } from './api';
import { fileValidation, SIZE_LIMIT_NUMBER } from './file-validations';
import type { MediaInputProps } from './type';

export function useImageInput({
  onChange,
  onImageStateChange,
  value,
  folder_path,
  access_level = 'private',
  disabled,
  name,
  maxHeight,
  maxWidth,
  width,
  height,
  formItemClassName,
  label,
  shape,
  size,
  asArray = true,
  mode = 'UPDATE',
  addPadding,
  hideLabel = false,
  type,
  accept,
  onImageSelection,
}: MediaInputProps) {
  const { mutateAsync } = useCreateFileMutation();
  const modelOpen = useBoolean();
  const [isCreating, create] = useBoolean();

  const [file, setFile] = useState<File | null>(null);
  const [fileIssue, setFileIssueState] = useState<undefined | string>(undefined);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, open] = modelOpen;

  const { imageUrl, singleValue, isRemovable, isUploadable, isDownloadable, filePreviewUrl } = useMemo(() => {
    const isArray = Array.isArray(value);
    const _singleValue = isArray ? value?.[0] : value;

    let _imageUrl = _singleValue?.url;
    if (mode === 'CREATE' && file) {
      _imageUrl = URL.createObjectURL(file);
    }

    const _isRemovable = !disabled && !file && _singleValue;
    const _isUploadable = !disabled && file;
    const _filePreviewUrl = file ? URL.createObjectURL(file) : (_imageUrl ?? '');

    return {
      imageUrl: _imageUrl,
      singleValue: _singleValue,
      isRemovable: _isRemovable,
      isUploadable: _isUploadable,
      isDownloadable: !!_singleValue?.url,
      filePreviewUrl: _filePreviewUrl,
    };
  }, [disabled, file, mode, value]);

  const onCancel = useCallback(() => {
    open.setFalse();
    if (inputRef.current) {
      inputRef.current.value = '';
      setFileIssueState(undefined);
      setFile(null);
    }
  }, [open]);

  const onHandleOpen = useCallback(() => {
    if (singleValue) {
      openFile(singleValue.url, singleValue?.name ?? '');
    }
  }, [singleValue]);

  const handleCloseOnly = useCallback(() => {
    open.setFalse();
  }, [open]);

  const onUpload = useCallback(() => {
    if (file) {
      create.setTrue();

      mutateAsync(
        {
          name: file.name,
          access_level,
          folder_path,
          media: file,
        },
        {
          onSettled: async (response) => {
            if (response) {
              const { data } = response;
              if (!singleValue) {
                await onImageStateChange('CREATE', name || '', [data]);
              } else {
                await onImageStateChange('UPDATE', name || '', [data]);
              }
              onChange(asArray ? [data] : data);
              onCancel();
            }
            create.setFalse();
          },
        }
      );
    }
  }, [
    access_level,
    asArray,
    create,
    file,
    folder_path,
    mutateAsync,
    name,
    onCancel,
    onChange,
    onImageStateChange,
    singleValue,
  ]);

  const onDelete = useCallback(async () => {
    if (singleValue) {
      await onImageStateChange('DELETED', name || '', [singleValue]);
      onChange([]);
      onCancel();
    }
  }, [singleValue, onImageStateChange, name, onChange, onCancel]);

  const handleImageSelector = useCallback(() => {
    if (mode === 'CREATE' && file && !isOpen) {
      return open.setTrue();
    }

    if (singleValue && !isOpen) {
      open.setTrue();
    } else if (!disabled && inputRef.current) {
      inputRef.current.click();
    }

    return undefined;
  }, [disabled, file, isOpen, mode, open, singleValue]);

  const handleFileChange = useCallback(
    async ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { files } = target;
      if (files?.length) {
        const selectedFiles = files as FileList;
        open.setTrue();

        const res = await fileValidation(selectedFiles[0], maxWidth, maxHeight, width, height, accept);

        switch (res) {
          case 'SIZE_LIMIT_REACHED':
            setFileIssueState(`The selected file exceeds the maximum allowed size of ${SIZE_LIMIT_NUMBER}MB.`);
            break;
          case 'INVALID_FILE_TYPE':
            setFileIssueState(`Only ${accept} file(s) are accepted.`);
            break;
          case 'WIDTH_LIMIT_REACHED':
            setFileIssueState(`The width of the selected image must be less than ${maxWidth}px.`);
            break;
          case 'HEIGHT_LIMIT_REACHED':
            setFileIssueState(`The height of the selected image must be less than ${maxHeight}px.`);
            break;
          case 'DIMENSION_MISMATCH':
            setFileIssueState(
              `The dimensions of selected image doesn't meet the requirements.<br/>${width}px x ${height}px (width x height)`
            );
            break;
          default:
            setFileIssueState(undefined);
            break;
        }

        setFile(selectedFiles[0]);
      }
    },
    [accept, height, maxHeight, maxWidth, open, width]
  );

  useEffect(() => {
    onImageSelection?.(file);
  }, [file, onImageSelection]);

  return {
    handleImageSelector,
    onUpload,
    onHandleOpen,
    onCancel,
    onDelete,
    handleFileChange,
    handleCloseOnly,
    mode,
    inputRef,
    fileIssue,
    modelOpen,
    file,
    isPending: isCreating,
    isDeleting: false, // TODO: Fix it if the delete file mutation is used
    singleValue,
    imageUrl,
    isRemovable,
    isUploadable,
    isDownloadable,
    filePreviewUrl,
    formItemClassName,
    label,
    disabled,
    shape,
    size,
    addPadding,
    hideLabel,
    type,
  };
}

export type UseImageInputReturn = ReturnType<typeof useImageInput>;
